
import React, { useState } from "react";
import { RibaStage, Project } from "@/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Check, File, FileCheck, Plus } from "lucide-react";
import { FileUpload } from "./FileUpload";

interface StageOutputsProps {
  project: Project;
  stage: RibaStage;
  onUpdate?: () => void;
}

// Define the OutputStatus type to track file uploads and comments
interface OutputStatus {
  [key: string]: {
    status: "pending" | "completed";
    file?: string;
    comments?: string;
  };
}

// Define expected outputs by stage
const stageOutputs: Record<number, { name: string; description: string; required: boolean }[]> = {
  0: [
    { name: "Strategic Brief", description: "High-level strategic brief documenting client requirements and project objectives", required: true },
  ],
  1: [
    { name: "Project Brief", description: "Detailed project brief including objectives, sustainability goals, and spatial needs", required: true },
    { name: "Risk Register", description: "Initial risk assessment and mitigation strategy", required: true },
    { name: "Project Execution Plan", description: "Plan detailing project management approach", required: true },
  ],
  2: [
    { name: "Concept Design Pack", description: "Complete concept design documentation", required: true },
    { name: "Outline Cost Plan", description: "Initial cost estimates and budget breakdown", required: true },
    { name: "Stage 2 Risk Register", description: "Updated risk assessment with design-specific risks", required: true },
  ],
  3: [
    { name: "Spatially Coordinated Design Package", description: "Complete spatially coordinated design documentation", required: true },
    { name: "Detailed Specifications", description: "Detailed specifications for all elements and systems", required: true }, 
    { name: "Updated Cost Plan", description: "Refined cost plan based on detailed design", required: true },
    { name: "Planning Submission Package", description: "Complete planning application documentation (if applicable)", required: true },
    { name: "Change Control Log", description: "Documentation of design changes and approvals", required: true },
  ],
  4: [
    { name: "Technical Design Package", description: "Complete technical design suitable for construction", required: true },
    { name: "Final Specifications Document", description: "Final detailed specifications for all elements", required: true },
    { name: "Manufacturing Information", description: "Information for off-site manufacturing", required: true },
    { name: "Building Regulations Submission", description: "Building control application and documentation", required: true },
    { name: "Tender Documentation", description: "Complete tender package (if applicable)", required: true },
  ],
  5: [
    { name: "Construction Progress Reports", description: "Regular progress reports from site", required: true },
    { name: "Quality Inspection Records", description: "Records of quality inspections and tests", required: true },
    { name: "Site Instructions Log", description: "Log of all formal instructions issued", required: true },
    { name: "Progress Photographs", description: "Photographic record of construction progress", required: true },
    { name: "Health & Safety File Updates", description: "Regular updates to the health and safety file", required: true },
  ],
  6: [
    { name: "Practical Completion Certificate", description: "Formal certification of practical completion", required: true },
    { name: "As-Built Drawings", description: "Final as-built drawings of all elements", required: true },
    { name: "Operation & Maintenance Manuals", description: "Complete O&M documentation", required: true },
    { name: "Building User Guide", description: "Guide for occupants on building systems", required: true },
    { name: "Defects List", description: "Comprehensive list of identified defects", required: true },
  ],
  7: [
    { name: "Post-Occupancy Evaluation Report", description: "Analysis of building performance in use", required: true },
    { name: "Sustainability Performance Report", description: "Assessment of actual sustainability outcomes", required: true },
    { name: "User Satisfaction Survey", description: "Feedback from building users", required: true },
    { name: "Lessons Learned Report", description: "Documented lessons for future projects", required: true },
    { name: "Final Project Review", description: "Comprehensive review of project outcomes", required: true },
  ],
};

export default function StageOutputs({ project, stage, onUpdate }: StageOutputsProps) {
  const [outputStatus, setOutputStatus] = useState<OutputStatus>({});
  const [currentOutput, setCurrentOutput] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Get the expected outputs for this stage
  const expectedOutputs = stageOutputs[stage.id] || [];
  
  const handleUpload = (outputName: string, url: string) => {
    setOutputStatus(prev => ({
      ...prev,
      [outputName]: {
        ...prev[outputName],
        status: url ? "completed" : "pending",
        file: url
      }
    }));
    
    toast({
      title: "Output updated",
      description: `${outputName} has been updated successfully.`
    });
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const handleSaveComment = () => {
    if (!currentOutput) return;
    
    setOutputStatus(prev => ({
      ...prev,
      [currentOutput]: {
        ...prev[currentOutput],
        comments: comment
      }
    }));
    
    toast({
      title: "Comment saved",
      description: `Comment for ${currentOutput} has been saved.`
    });
    
    setComment("");
    setCurrentOutput(null);
    setIsDialogOpen(false);
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const openCommentDialog = (outputName: string) => {
    setCurrentOutput(outputName);
    setComment(outputStatus[outputName]?.comments || "");
    setIsDialogOpen(true);
  };
  
  // Determine if stage is locked (completed)
  const isLocked = stage.status === 'completed';
  
  if (expectedOutputs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Required Outputs</CardTitle>
          <CardDescription>This stage does not have specified outputs.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Required Outputs</CardTitle>
          <CardDescription>Deliverables that must be completed for this stage</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {expectedOutputs.map((output) => {
              const status = outputStatus[output.name]?.status || "pending";
              return (
                <div key={output.name} className="border rounded-md p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium">{output.name}</h3>
                      <p className="text-sm text-muted-foreground">{output.description}</p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      status === "completed" 
                        ? "bg-green-100 text-green-800" 
                        : "bg-amber-100 text-amber-800"
                    }`}>
                      {status === "completed" ? (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          Complete
                        </>
                      ) : (
                        "Pending"
                      )}
                    </span>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <FileUpload 
                      id={`output-${output.name}`}
                      onUpload={(url) => handleUpload(output.name, url)}
                      disabled={isLocked}
                    />
                    
                    <div className="flex justify-end space-x-2 mt-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openCommentDialog(output.name)}
                        disabled={isLocked}
                      >
                        Add Comments
                      </Button>
                    </div>
                    
                    {outputStatus[output.name]?.comments && (
                      <div className="mt-2 text-sm bg-muted p-2 rounded-md">
                        <p className="font-medium">Comments:</p>
                        <p>{outputStatus[output.name].comments}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Comments</DialogTitle>
            <DialogDescription>
              Add comments or notes about this output.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter your comments here..."
              className="min-h-[100px]"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveComment}>Save Comments</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
